import { ApiUnreachableError, AuthorizationError, type ApiRequestError } from '@/api/client'

/** A machine code ("onboarding_finished"), as opposed to a sentence some routes put in `error`. */
const CODE_PATTERN = /^[a-z][a-z0-9_]*$/

/** The `{ code, message }` (or `{ error, message }`) body admin-api sends with a refusal. */
export interface ApiErrorBody {
  code: string | null
  message: string | null
}

/** The parsed JSON body of a failed request, when it had one in admin-api's shape. */
export function apiErrorBody(err: unknown): ApiErrorBody | null {
  const body = (err as ApiRequestError | null | undefined)?.body
  if (!body || typeof body !== 'object') return null
  const record = body as Record<string, unknown>
  // Most routes send `code`; the link route sends its code as `error`. Other
  // routes put a sentence in `error`, which is not a code.
  const code =
    typeof record.code === 'string'
      ? record.code
      : typeof record.error === 'string' && CODE_PATTERN.test(record.error)
        ? record.error
        : null
  const message = typeof record.message === 'string' ? record.message : null
  if (code === null && message === null) return null
  return { code, message }
}

/**
 * admin-api's message for a refusal, shown as it was written. Several routes
 * prefix it with their code ("onboarding_finished: ..."); that prefix is for
 * logs and the CLI, so it is dropped here, leaving the sentence.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AuthorizationError) return 'You are not allowed to do this.'
  if (err instanceof ApiUnreachableError) return err.message
  const body = apiErrorBody(err)
  return body ? formatApiMessage(body.code, body.message, fallback) : fallback
}

/** One `{ code, message }` pair as a sentence: the code prefix dropped, capitalised. */
export function formatApiMessage(
  code: string | null,
  message: string | null,
  fallback: string
): string {
  if (!message) return fallback
  const prefix = code ? `${code}: ` : null
  const text = prefix && message.startsWith(prefix) ? message.slice(prefix.length) : message
  return text.trim() ? text.charAt(0).toUpperCase() + text.slice(1) : fallback
}
